import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

interface PageInfo {
  name: string;
  desc: string;
  pageTitle?: string;
}

interface PageNameContextInterface {
  pageInfo: PageInfo;
  setInfo: (info: PageInfo) => void;
}

interface PageNameProviderInterface {
  children: React.ReactNode;
}

const PageNameContext = createContext<PageNameContextInterface>({
  pageInfo: {
    name: "Page",
    desc: "This is a new page!",
  },
  setInfo: () => {},
});

export function PageNameProvider({ children }: PageNameProviderInterface) {
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    name: "Page",
    desc: "This is a new page!",
  });

  // Stable identity: pages list setInfo as an effect dependency, so recreating it
  // each render would re-run those effects and loop back into this state update.
  const _setPageInfo = useCallback((info: PageInfo) => {
    setPageInfo((current) =>
      current.name === info.name &&
      current.desc === info.desc &&
      current.pageTitle === info.pageTitle
        ? current
        : info,
    );

    if (info.pageTitle) {
      document.title = `Barney | ${info.pageTitle}`;
    }
  }, []);

  const value = useMemo(
    () => ({ pageInfo, setInfo: _setPageInfo }),
    [pageInfo, _setPageInfo],
  );

  return (
    <PageNameContext.Provider value={value}>
      {children}
    </PageNameContext.Provider>
  );
}

export function usePageNameContext() {
  const pageNameCtx = useContext(PageNameContext);

  if (!pageNameCtx) {
    throw new Error(
      "usePageNameContext must be used within a PageNameProvider",
    );
  }

  return pageNameCtx;
}
